import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import ModificationAlert from './ModificationAlert';
import { Button, Card, CardContent, Typography, Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { DeviceNameMapper } from '../utils/DeviceNameMapper';

const notificationStyles = {
  marginBottom: '20px',
  backgroundColor: '#2E2F45',
  color: '#EAEAEA',
  padding: '10px 20px',
  borderRadius: '10px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s, box-shadow 0.2s',
};

// IDs à ignorer (uniquement pour Aquacheck) - copié de TimeRangeAndDeviceSelector.js
const DEVICE_BLACKLIST = [
  '000000000400',
  '000000000401',
  '000000000402',
];

const AlertPage = ({
  alerts = [],
  onClearAlerts,
  onAlertClick,
  alertValue,
  setAlertValue,
  alertActivate,
  setAlertActivate,
  alertSent,
  setAlertSent,
  initialSelectedType = '',
  initialSelectedDevice = '',
  initialSelectionKey = 0,
}) => {
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState(() =>
    String(initialSelectedType || '').toLowerCase()
  );
  const [selectedDevice, setSelectedDevice] = useState(() =>
    initialSelectedDevice ? [String(initialSelectedDevice)] : []
  ); // Array for multi-select
  const [filterDate, setFilterDate] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  useEffect(() => {
    const normalizedType = String(
      initialSelectedType || ''
    ).toLowerCase();

    const normalizedDevice = String(
      initialSelectedDevice || ''
    );

    if (!normalizedType || !normalizedDevice) {
      return;
    }

    setSelectedType(normalizedType);
    setSelectedDevice([normalizedDevice]);
    setFilterDate(null);
    setError('');
  }, [
    initialSelectedType,
    initialSelectedDevice,
    initialSelectionKey,
  ]);

  // Charger les boîtiers quand le type change
  useEffect(() => {
    const fetchDevices = async () => {
      if (!selectedType) {
        setAvailableDevices([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${API_URL}/api/devices?table=${selectedType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAvailableDevices(
          Array.isArray(response.data)
            ? response.data.map((deviceId) => String(deviceId))
            : []
        );
      } catch (error) {
        console.error('Erreur lors de la récupération des boîtiers:', error);
        setAvailableDevices([]);
      }
    };

    fetchDevices();
  }, [selectedType, API_URL]);

  const mapper = useMemo(
    () => {
      // On filtre les boîtiers pour le mapper afin que la numérotation commence à 1 pour les boîtiers valides
      let devicesForMapper = availableDevices;
      if (selectedType === 'aquacheck') {
        devicesForMapper = availableDevices.filter((id) => !DEVICE_BLACKLIST.includes(id));
      }
      return new DeviceNameMapper(selectedType, devicesForMapper);
    },
    [selectedType, availableDevices]
  );

  const filteredDevices = useMemo(() => {
    if (!availableDevices) return [];

    // Toujours copier le tableau pour éviter de muter l'état et pour garantir que le tri fonctionne
    let devices = [...availableDevices];

    // On applique le même filtre que pour le mapper pour la liste déroulante
    if (selectedType === 'aquacheck') {
      devices = devices.filter((id) => !DEVICE_BLACKLIST.includes(id));
    }

    // Tri "humain" basé sur le nom généré (ex: "Aquacheck 4" avant "Aquacheck 10")
    return devices.sort((a, b) => {
      const aliasA = mapper.getAlias(a);
      const aliasB = mapper.getAlias(b);

      // Extraction du numéro dans le nom (ex: "Aquacheck 10" -> 10)
      const numA = parseInt(aliasA.match(/\d+/)?.[0] || 0, 10);
      const numB = parseInt(aliasB.match(/\d+/)?.[0] || 0, 10);

      return numA - numB;
    });

  }, [availableDevices, selectedType, mapper]);

  const handleClearAlerts = () => {
    try {
      if (alerts.length === 0) {
        throw new Error('Aucune alerte à effacer.');
      }
      if (typeof onClearAlerts === 'function') {
        onClearAlerts();
        setError('');
      } else {
        throw new Error('La fonction onClearAlerts est invalide ou manquante.');
      }
    } catch (err) {
      setError(err.message);
    }
  };
  const handleSaveAlerts = async () => {
  try {
    setError('');

    // Il faut un type + au moins 1 boîtier sélectionné (ton state selectedDevice est un array)
    if (!selectedType) {
      setError("Choisis un type de boîtier avant d'enregistrer.");
      return;
    }
    if (!selectedDevice || selectedDevice.length === 0) {
      setError("Choisis au moins un boîtier avant d'enregistrer.");
      return;
    }

    const token = localStorage.getItem('token');

    // On transforme alertValue + alertActivate en tableau "thresholds"
    // - si alertValue[key] est [min,max] => on envoie min/max
    // - sinon => on met min=null, max=value (cas capteur à valeur unique)
    const thresholds = Object.keys(alertValue).map((key) => {
      const v = alertValue[key];
      const enabled = alertActivate?.[key] ?? true;

      if (Array.isArray(v)) {
        return { metric: key, min: v[0], max: v[1] };
      }
      return { metric: key, min: null, max: v };
    });

    // On enregistre pour chaque boîtier sélectionné
    await Promise.all(
      selectedDevice.map((deviceId) =>
        axios.post(
          `${API_URL}/api/alerts/thresholds/bulk`,
          {
            tableName: selectedType,
            deviceId: String(deviceId),
            thresholds,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      )
    );

    // Option : petit feedback (tu peux remplacer par un snackbar)
    setError('');
  } catch (e) {
    console.error(e);
    const msg = e?.response?.data?.error || e.message || "Erreur lors de l'enregistrement.";
    setError(msg);
  }
};

  const filteredAlerts = alerts.filter((alert) => {
    // Filtre par type (sensorName correspond au type/table)
    const matchesType = selectedType
      ? alert.sensorName === selectedType
      : true;

    // Filtre par ID (multi-select)
    const matchesDevice = selectedDevice.length > 0
      ? selectedDevice.includes(String(alert.sensorId))
      : true;

    let matchesDate = true;
    if (filterDate) {
      const alertDate = alert.rawTimestamp ? new Date(alert.rawTimestamp) : null;
      if (alertDate) {
        matchesDate = dayjs(alertDate).isSame(filterDate, 'day');
      } else {
        matchesDate = false;
      }
    }
    return matchesType && matchesDevice && matchesDate;
  });

  const selectStyle = {
    backgroundColor: '#3E3F5E',
    borderRadius: '5px',
    color: '#EAEAEA',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#888' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF5733' },
    '& .MuiSelect-icon': { color: '#EAEAEA' },
    '& .MuiInputBase-input': { color: '#EAEAEA' },
  };

  const inputLabelStyle = { color: '#aaa', '&.Mui-focused': { color: '#FF5733' } };

  return (
    <div style={{ padding: '20px', color: '#EAEAEA', maxWidth: '1000px', margin: 'auto' }}>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
  <Button
    onClick={handleClearAlerts}
    variant="contained"
    sx={{
      backgroundColor: alerts.length > 0 ? '#FF5733' : '#555',
      color: alerts.length > 0 ? '#fff' : '#888',
      fontWeight: 'bold',
      borderRadius: '10px',
      px: 3,
      py: 1.5,
      transition: 'background-color 0.3s ease-in-out',
      cursor: alerts.length > 0 ? 'pointer' : 'not-allowed',
    }}
    disabled={alerts.length === 0}
    aria-label="Effacer les alertes"
  >
    Effacer les alertes
  </Button>

  <Button
    onClick={handleSaveAlerts}
    variant="outlined"
    sx={{
      borderColor: 'rgba(255,255,255,0.25)',
      color: '#fff',
      fontWeight: 'bold',
      borderRadius: '10px',
      px: 3,
      py: 1.5,
      '&:hover': {
        borderColor: 'rgba(255,255,255,0.45)',
        backgroundColor: 'rgba(255,255,255,0.06)',
      },
    }}
    aria-label="Enregistrer les alertes"
  >
    Enregistrer les alertes
  </Button>
</Box>

      {/* ModificationAlert component */}
      <ModificationAlert
        alertActivate={alertActivate}
        setAlertActivate={setAlertActivate}
        alertValue={alertValue}
        setAlertValue={setAlertValue}
        alertSent={alertSent}
        setAlertSent={setAlertSent}
      />

      <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
        Alertes
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>

        {/* Type Selector */}
        <FormControl sx={{ minWidth: 150 }} variant="outlined">
          <InputLabel sx={inputLabelStyle}>Type de boîtier</InputLabel>
          <Select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              setSelectedDevice([]); // Reset device when type changes
            }}
            label="Type de boîtier"
            sx={selectStyle}
            MenuProps={{ PaperProps: { sx: { bgcolor: '#2E2F45', color: '#EAEAEA', maxHeight: 300 } } }}
          >
            <MenuItem value=""><em>Tous</em></MenuItem>
            <MenuItem value="climatrack">Climatrack</MenuItem>
            <MenuItem value="aquacheck">Aquacheck</MenuItem>
            <MenuItem value="greenwatch">Greenwatch</MenuItem>
          </Select>
        </FormControl>

        {/* Device Selector (Multi-select enabled if Type is selected) */}
        <FormControl sx={{ minWidth: 250, maxWidth: 400 }} variant="outlined" disabled={!selectedType}>
          <InputLabel sx={inputLabelStyle}>Boîtier (Nom/ID)</InputLabel>
          <Select
            multiple
            value={selectedDevice}
            onChange={(e) => {
              const { target: { value } } = e;
              // On autofill we get a stringified value.
              setSelectedDevice(
                typeof value === 'string' ? value.split(',') : value,
              );
            }}
            label="Boîtier (Nom/ID)"
            sx={selectStyle}
            MenuProps={{ PaperProps: { sx: { bgcolor: '#2E2F45', color: '#EAEAEA', maxHeight: 300 } } }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>Tous</em>;
              }
              // Affiche une liste concise des alias sélectionnés
              return selected.map(id => mapper.getAlias(id)).join(', ');
            }}
          >
            {filteredDevices.map((deviceId) => {
              const alias = mapper.getAlias(deviceId);
              return (
                <MenuItem key={deviceId} value={deviceId}>
                  {alias} (ID: {deviceId})
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Filtrer par date"
            value={filterDate}
            onChange={(newValue) => setFilterDate(newValue)}
            slotProps={{
              textField: {
                sx: {
                  backgroundColor: '#3E3F5E',
                  borderRadius: '5px',
                  input: { color: '#EAEAEA' },
                  label: { color: '#aaa' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#888' },
                    '&.Mui-focused fieldset': { borderColor: '#FF5733' },
                  },
                  svg: { color: '#EAEAEA' }
                }
              }
            }}
          />
        </LocalizationProvider>

        <Button
          variant="outlined"
          onClick={() => { setSelectedType(''); setSelectedDevice([]); setFilterDate(null); }}
          sx={{ color: '#EAEAEA', borderColor: '#555', '&:hover': { borderColor: '#FF5733', color: '#FF5733' } }}
        >
          Réinitialiser
        </Button>
      </Box>

      {error && (
        <Typography variant="body1" sx={{ color: 'red', textAlign: 'center', mb: 1 }}>
          {error}
        </Typography>
      )}

      {filteredAlerts.length === 0 ? (
        <Typography variant="h6" sx={{ textAlign: 'center' }}>
          {alerts.length === 0 ? "Aucune alerte pour le moment." : "Aucune alerte ne correspond aux filtres."}
        </Typography>
      ) : (
        filteredAlerts.map((alert, index) => (
          <Card
            key={index}
            sx={{
              ...notificationStyles,
              border: alert.read ? '1px solid #555' : '1px solid #FF5733',
              backgroundColor: alert.read ? '#2E2F45' : '#3E3F5E',
              cursor: 'pointer',
            }}
            onClick={() => onAlertClick(alerts.indexOf(alert))}
            aria-label={`Alerte ${alert.title}`}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#EAEAEA' }}>
                {alert.title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {alert.message}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#FF5733' }}>
                Valeur détectée: {alert.value} - {alert.recommendation}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#aaa' }}>
                {/* Si on a le type sélectionné et que l'ID correspond, on peut afficher l'alias, sinon juste l'ID et le type */}
                {/* Mais on peut tenter d'afficher l'alias si on a la liste chargée, sinon fallback */}
                Capteur: {selectedType && availableDevices.includes(String(alert.sensorId)) ? mapper.getAlias(String(alert.sensorId)) : (alert.sensorName || 'N/A')} (ID: {alert.sensorId || 'N/A'})
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#FF5733' }}>
                Détecté le: {alert.timestamp}
              </Typography>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default AlertPage;